import { useCallback, useRef, useState } from "react";
import { throwResponseError } from "../../utils/api-error-utils";
import { getAuthParams } from "../../utils/pacto-rebates-utils";
import {
    formatAssessmentDecisionResponse
} from "../utils/installments-assessment-utils";

const DECISION_STATUSES = ["APROVADO", "REPROVADO"];

export function useAssessmentDecision() {
    const [decision, setDecision] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const isSubmittingRef = useRef(false);

    const decideAssessments = useCallback(async ({
        cycleId,
        status,
        rejectionDescription
    } = {}) => {
        // Impede dois envios simultâneos pela mesma instância do hook.
        if (isSubmittingRef.current) {
            return {
                ok: false,
                reason: "pending",
                error: "Já existe uma decisão sendo enviada."
            };
        }

        if (!cycleId) {
            const message = "Informe o ciclo da apuração.";

            setDecision(null);
            setError(message);

            return {
                ok: false,
                reason: "validation",
                error: message
            };
        }

        if (!DECISION_STATUSES.includes(status)) {
            const message = "Informe uma decisão válida: APROVADO ou REPROVADO.";

            setDecision(null);
            setError(message);

            return {
                ok: false,
                reason: "validation",
                error: message
            };
        }

        const description = String(rejectionDescription ?? "").trim();

        if (status === "REPROVADO" && !description) {
            const message = "Informe a descrição da reprovação.";

            setDecision(null);
            setError(message);

            return {
                ok: false,
                reason: "validation",
                error: message
            };
        }

        isSubmittingRef.current = true;

        try {
            setLoading(true);
            setError(null);
            setDecision(null);

            const { accessToken, clientId } = getAuthParams();

            const payload = {
                status
            };

            if (status === "REPROVADO") {
                payload.descricaoReprovacao = description;
            }

            const response = await fetch(
                process.env.REACT_APP_ESSILOR_API +
                `pacto-rebates-apuracao/v1/apuracoes/${encodeURIComponent(cycleId)}/decisao`,
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                        "access_token": accessToken,
                        "client_id": clientId
                    },
                    body: JSON.stringify(payload)
                }
            );

            if (!response.ok) {
                await throwResponseError(
                    response,
                    "Não foi possível registrar a decisão da apuração."
                );
            }

            const data = await response.json();
            const formattedDecision =
                formatAssessmentDecisionResponse(data);

            setDecision(formattedDecision);

            return {
                ok: true,
                decision: formattedDecision
            };
        } catch (requestError) {
            const errorMessage =
                requestError.message ||
                "Não foi possível registrar a decisão da apuração.";

            setDecision(null);
            setError(errorMessage);

            return {
                ok: false,
                reason: "request",
                error: errorMessage
            };
        } finally {
            isSubmittingRef.current = false;
            setLoading(false);
        }
    }, []);

    return {
        decision,
        loading,
        error,
        decideAssessments
    };
}